/**
 * KYCReminderBanner (v37 — May 21, 2026)
 *
 * Shows a dismissible banner at the top of the dashboard if the current user's KYC profile
 * is incomplete. Dismissal is session-scoped (sessionStorage) so it re-appears on next login
 * — that's the intended UX per the customer ask ("they should be reminded every time they
 * login").
 *
 * Hidden when:
 *   - User is super admin
 *   - User has no staff_profile (e.g., patient login)
 *   - All KYC fields already filled (is_complete=true)
 *   - User dismissed for this session
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v37API } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { ShieldAlert, X, ArrowRight } from 'lucide-react';

const SKEY = 'kyc_reminder_dismissed';

export const KYCReminderBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(SKEY) === '1');

  const enabled = !!user && !user.is_super_admin && !!user.hospital_id;

  useEffect(() => {
    if (!enabled) return;
    let mounted = true;
    v37API.kycStatus()
      .then(res => { if (mounted) setStatus(res.data); })
      .catch(() => { /* fail silently — banner just won't appear */ });
    return () => { mounted = false; };
  }, [enabled]);

  if (!enabled || dismissed || !status || status.is_complete || status.not_applicable) return null;

  const requiredCount = status.missing_required?.length || 0;
  const encouragedCount = status.missing_encouraged?.length || 0;
  const totalMissing = requiredCount + encouragedCount;

  return (
    <div className="mb-4 rounded-xl border border-amber-300 dark:border-amber-700/40 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 shadow-sm" data-testid="kyc-reminder-banner">
      <div className="flex items-start gap-3 p-4">
        <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700/40 flex items-center justify-center shrink-0">
          <ShieldAlert size={20} className="text-amber-600" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Complete Your KYC &amp; Profile</h3>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
              {status.completion_pct}% complete
            </span>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
            {requiredCount > 0 && (
              <>
                <b className="text-amber-700 dark:text-amber-400">{requiredCount} required field{requiredCount > 1 ? 's' : ''}</b>
                {' '}({status.missing_required.map(f => f.label).join(', ')})
                {encouragedCount > 0 && ' · '}
              </>
            )}
            {encouragedCount > 0 && (
              <>
                <span className="text-slate-500">{encouragedCount} optional</span>
                {' '}({status.missing_encouraged.map(f => f.label).join(', ')})
              </>
            )}
            {' '}— takes ~2 minutes to fill.
          </p>

          {/* Progress bar */}
          <div className="mt-2 w-full max-w-md h-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
              style={{ width: `${status.completion_pct}%` }} />
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            className="h-8 px-3 gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => navigate('/my-profile')}
            data-testid="kyc-banner-fill-btn">
            Complete Now <ArrowRight size={12} />
          </Button>
          <button
            onClick={() => { sessionStorage.setItem(SKEY, '1'); setDismissed(true); }}
            className="h-8 w-8 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
            title="Dismiss for this session (will return on next login)"
            data-testid="kyc-banner-dismiss-btn">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default KYCReminderBanner;
